import React, { useEffect, useState } from 'react';
import axios, { API } from '../../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain } from '@phosphor-icons/react';

/**
 * ChatContextBanner — Phase 5B.
 *
 * On first load: thin strip showing what Kindra has in context.
 *   e.g.  "Kindra has in context: Sleep trend · Lithium · Upcoming GP visit · Recent symptoms"
 *
 * If server flags `welcome_back`: shows a gentle note instead.
 *   e.g.  "Welcome back. I noticed a few things worth discussing."
 *
 * After `collapsed` becomes true (parent sets it once the user sends the first
 * message), collapses to a subtle reassurance line:
 *   "I'm caught up on your week."
 *
 * Parent controls collapsed via the `collapsed` prop.
 */
export const ChatContextBanner = ({ collapsed = false }) => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/chat/context-banner`);
        if (!cancelled) setData(d);
      } catch {
        /* silent — absence of banner is acceptable */
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // While loading, reserve vertical space with a subtle skeleton so the
  // input position doesn't jump when the banner resolves.
  if (!loaded) {
    return (
      <div
        className="mb-3 h-[34px] rounded-lg bg-[#FBFAF6] border border-[#ECE8E1] animate-pulse"
        data-testid="chat-context-banner-skeleton"
      />
    );
  }

  if (!data) return null;

  // Collapsed subtle reassurance line
  if (collapsed) {
    return (
      <AnimatePresence>
        <motion.p
          key="collapsed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="mb-3 text-[12px] text-slate-500 flex items-center gap-1.5"
          data-testid="chat-context-banner-collapsed"
        >
          <Brain size={12} weight="regular" className="text-[#2FB3A0]" />
          {data.collapsed_text || "I'm caught up on your week."}
        </motion.p>
      </AnimatePresence>
    );
  }

  // Welcome-back variant
  if (data.welcome_back && data.welcome_back_message) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="mb-3 rounded-lg border border-[#2FB3A0]/25 bg-[#2FB3A0]/[0.06] px-3.5 py-2.5 flex items-start gap-2.5"
        data-testid="chat-context-banner-welcome-back"
      >
        <Brain size={14} weight="fill" className="text-[#2FB3A0] flex-shrink-0 mt-[2px]" />
        <p className="text-[13.5px] text-[#0E1C2E] leading-relaxed">
          {data.welcome_back_message}
        </p>
      </motion.div>
    );
  }

  // Default: thin in-context strip
  // Stage 19 — copy upgraded from "Kindra has in context" → "Using your profile:"
  // so the trust strip reads as grounded personalisation, not a feature claim.
  // When reports are also in context, the API surfaces them in `items`; the
  // label stays "Using your profile" (same trust frame).
  // Stage 20B+ — sanitize dev-test labels (uppercase `TEST_*` / `_TEST_*`)
  // before they ever surface in the trust strip. The pattern is intentionally
  // strict (uppercase `TEST` followed by underscore) so legitimate labels like
  // "Test Med" or "Stress Test" pass through untouched.
  const DEV_TEST_PREFIX = /^_?TEST_+/;
  const items = (Array.isArray(data.items) ? data.items : [])
    .filter(Boolean)
    .map((it) => {
      const s = String(it || '');
      return DEV_TEST_PREFIX.test(s) ? s.replace(DEV_TEST_PREFIX, '').trim() : s;
    })
    .filter((it) => String(it).trim().length > 0);
  if (items.length === 0) return null;
  const hasReports = items.some((it) => /report|lab|scan|letter/i.test(String(it)));
  const label = hasReports ? "Using your profile and reports:" : "Using your profile:";

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="mb-3 rounded-lg border border-[#ECE8E1] bg-[#FBFAF6] px-3.5 py-2 flex items-start gap-2"
      data-testid="chat-context-banner"
    >
      <Brain size={12} weight="regular" className="text-slate-400 flex-shrink-0 mt-[4px]" />
      <p className="text-[12px] text-slate-600 leading-relaxed">
        <span className="font-semibold text-[#0E1C2E] tracking-[0.01em]">{label}</span>
        <span className="mx-1.5 text-slate-300"> </span>
        {items.map((it, i) => (
          <React.Fragment key={i}>
            <span>{it}</span>
            {i < items.length - 1 && <span className="mx-1.5 text-slate-300">·</span>}
          </React.Fragment>
        ))}
      </p>
    </motion.div>
  );
};

export default ChatContextBanner;
